import { inject, Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { PermissionRole, PermissionValues, User } from 'app/core/models';
import { SetUser, SetUsers, SetUserStyleSettings, UpdateUserDefaultItemsPerPage } from './actions';
import { NewPermission } from 'app/core/enums';
import { AccountState } from '@state/account/state';
import { UserService } from 'app/layout/common/user/user.service';
import { tap } from 'rxjs';

export interface UserStateModel {
  user: User;
  users: User[];
  styleSettings: 'compact' | 'small' | 'normal';
}

@State<UserStateModel>({
  name: new StateToken<UserStateModel>('userState'),
  defaults: { user: null, users: null, styleSettings: null },
})
@Injectable()
export class UserState {
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);

  @Selector()
  static isTermSheetUser(state: UserStateModel): boolean {
    return (
      state.user?.email?.includes('@termsheet.com') ||
      state.user?.email?.includes('@intapp.com') ||
      state.user?.email?.includes('localdev@ts.com')
    );
  }

  @Selector()
  static getUser(state: UserStateModel): User {
    return state.user;
  }

  @Selector()
  static usersById(state: UserStateModel): Record<number, User> {
    return state.users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});
  }

  @Selector()
  static getUsers(state: UserStateModel): User[] {
    return state.users;
  }

  @Selector()
  static getActiveUsers(state: UserStateModel): User[] {
    return state.users.filter((user) => user?.active);
  }

  @Selector()
  static getUserDefaultItemsPerPage(state: UserStateModel): number {
    return state.user?.default_per_page || 20; // Default to 20 items per page if not set
  }

  @Selector()
  static getUserPermissionRoles(state: UserStateModel): PermissionRole[] {
    return state.user?.roles[0]?.permissions_roles.filter(
      ({ permission_value }) => PermissionValues.truthy[permission_value],
    );
  }

  @Selector()
  static getUserStyleSettings(state: UserStateModel): 'compact' | 'small' | 'normal' {
    return state.styleSettings;
  }

  @Selector([UserState.getUserPermissionRoles])
  static getUserPermissionNames(state: UserStateModel, roles: PermissionRole[]): (NewPermission | string)[] {
    const result = roles?.map(({ permission }) => permission.name as NewPermission) ?? [];

    return result;
  }

  static hasPermission(permission: NewPermission | string, onlyOne = false) {
    return createSelector(
      [UserState.getUserPermissionNames],
      (userPermissionNames: (NewPermission | string)[]): boolean => {
        if (typeof permission === 'string' && permission.startsWith('CO_')) {
          const hasCustomPermission = userPermissionNames.includes(permission);
          if (hasCustomPermission && onlyOne) {
            return userPermissionNames.length === 1;
          }
          return hasCustomPermission;
        }

        const hasPermission = userPermissionNames.includes(permission);

        if (hasPermission && onlyOne) {
          return userPermissionNames.length === 1;
        }

        return hasPermission;
      },
    );
  }

  @Action(SetUser)
  setUser(context: StateContext<UserStateModel>, { user }: { user: User }) {
    const state = context.getState();
    context.setState({ ...state, user });
  }

  @Action(SetUsers)
  setUsers(context: StateContext<UserStateModel>, { users }: { users: User[] }) {
    const state = context.getState();
    context.setState({ ...state, users });
  }

  @Action(UpdateUserDefaultItemsPerPage)
  updateUserDefaultItemsPerPage(context: StateContext<UserStateModel>, { pageSize }: { pageSize: number }) {
    const updatedUser: User = { ...context.getState().user, default_per_page: pageSize };

    return this.userService.update(updatedUser).pipe(
      tap(() => {
        // Here we need to persist the updatedUser in the store because the service response doesn't return the user's 'roles' attribute.
        context.setState({ ...context.getState(), user: updatedUser });
      }),
    );
  }

  @Action(SetUserStyleSettings)
  setUserStyleSettings(
    context: StateContext<UserStateModel>,
    { styleSettings }: { styleSettings: 'compact' | 'small' | 'normal' },
  ) {
    const state = context.getState();
    context.setState({ ...state, styleSettings });
  }
}
